import { useQuery } from '@tanstack/react-query';

import { STALE_TIMES } from '#lib/queryConstants';

import { getCrmImportJob, getPipedriveImportJob } from '../../api/crmImportApi';

/**
 * Fetch an import job's status
 * @param {string} jobId
 * @returns {Promise<object>}
 */
async function fetchImportJobStatus(jobId) {
	try {
		return await getCrmImportJob(jobId);
	} catch (err) {
		if (err?.status === 404) {
			return { status: 'completed', summaryStale: true };
		}
		throw err;
	}
}

/**
 * Poll an async import job until it is no longer processing.
 *
 * @param {string|null} jobId - the import job id (null disables polling)
 * @param {object} [options]
 * @param {boolean} [options.enabled=true]
 * @returns {import('@tanstack/react-query').UseQueryResult}
 */
export function useImportJobPolling(jobId, { enabled = true } = {}) {
	return useQuery({
		queryKey: ['crm', 'import', 'job', jobId],
		queryFn: () => fetchImportJobStatus(jobId),
		enabled: Boolean(jobId) && enabled,
		staleTime: STALE_TIMES.REALTIME,
		refetchInterval: (query) => (query.state.data?.status === 'processing' ? STALE_TIMES.REALTIME : false),
	});
}

/**
 * Poll a Pipedrive migration job until it is no longer processing.
 *
 * @param {string|null} jobId - the migration job id (null disables polling)
 * @param {object} [options]
 * @param {boolean} [options.enabled=true]
 * @returns {import('@tanstack/react-query').UseQueryResult}
 */
export function usePipedriveJobPolling(jobId, { enabled = true } = {}) {
	return useQuery({
		queryKey: ['crm', 'import', 'pipedrive', 'job', jobId],
		queryFn: () => getPipedriveImportJob(jobId),
		enabled: Boolean(jobId) && enabled,
		staleTime: STALE_TIMES.REALTIME,
		refetchInterval: (query) => (query.state.data?.status === 'processing' ? STALE_TIMES.REALTIME : false),
	});
}
